import {
	AuthRoleEnum,
	OrderTypes,
	useDeleteAdminUserMutation,
	useDeleteManyAdminUserMutation,
	useGetAllAdminUserQuery,
} from '@codezo/graphql';
import { DataContainer, formatDateForTable } from '@codezo/ui';
import {
	ActionIcon,
	Avatar,
	Badge,
	Button,
	Checkbox,
	Group,
	Menu,
	Modal,
	Pagination,
	Paper,
	Select,
	Stack,
	Table,
	Text,
	TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
	IconChevronDown,
	IconChevronUp,
	IconDots,
	IconEdit,
	IconPlus,
	IconSearch,
	IconTrash,
	IconUsers,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { EditUserModal } from './EditUserModal';
import { InviteUserModal } from './InviteUserModal';

const sortableHeaderStyles = `
	.sortable-header .sort-icon-wrapper {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		margin-left: 4px;
		vertical-align: middle;
	}
	.sortable-header .sort-icon-hover {
		opacity: 0;
		transition: opacity 0.15s ease;
		display: inline-flex;
		align-items: center;
	}
	.sortable-header:hover .sort-icon-hover {
		opacity: 1;
	}
`;

export function UserManagementTable() {
	const [searchValue, setSearchValue] = useState('');
	const [debouncedSearch] = useDebouncedValue(searchValue, 300);
	const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [inviteModalOpen, setInviteModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editingUser, setEditingUser] = useState<{
		id: string;
		firstName?: string;
		lastName?: string;
		authenticationUser?: { email?: string | null; [key: string]: unknown } | null;
		[key: string]: unknown;
	} | null>(null);
	const [sortField, setSortField] = useState<string>('firstName');
	const [sortDirection, setSortDirection] = useState<OrderTypes>(OrderTypes.Asc);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(10);

	// Query for admin users
	const { data, loading, error, refetch } = useGetAllAdminUserQuery({
		variables: {
			input: {
				pagination: { currentPage, limit: pageSize },
				search: debouncedSearch || undefined,
				order: { field: sortField, direction: sortDirection },
			},
		},
		skip: false,
		errorPolicy: 'all',
		fetchPolicy: 'network-only',
	});

	// Delete mutations
	const [deleteUser] = useDeleteAdminUserMutation();
	const [deleteUsers] = useDeleteManyAdminUserMutation({
		onCompleted: (result) => {
			if (result.deleteManyAdminUser.success) {
				notifications.show({
					title: 'Users Deleted',
					message: `Successfully deleted ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}`,
					color: 'green',
				});
				setSelectedUsers([]);
				setDeleteModalOpen(false);
				refetch();
			} else {
				notifications.show({
					title: 'Delete Failed',
					message: result.deleteManyAdminUser.message || 'Failed to delete users',
					color: 'red',
				});
			}
		},
		onError: (err) => {
			notifications.show({
				title: 'Delete Failed',
				message: err.message || 'An error occurred while deleting users',
				color: 'red',
			});
		},
	});

	// Use all admin users
	const filteredUsers = useMemo(() => {
		return data?.getAllAdminUser?.data || [];
	}, [data?.getAllAdminUser?.data]);

	const selectAll = () => {
		if (selectedUsers.length === filteredUsers.length && filteredUsers.length > 0) {
			setSelectedUsers([]);
		} else {
			setSelectedUsers(filteredUsers.map((user) => user.id));
		}
	};

	const confirmDelete = async () => {
		try {
			if (deletingId) {
				// Single delete
				const result = await deleteUser({
					variables: { id: deletingId },
				});
				if (result.data?.deleteAdminUser?.success) {
					notifications.show({
						title: 'User Deleted',
						message: 'Successfully deleted user',
						color: 'green',
					});
					setDeleteModalOpen(false);
					setDeletingId(null);
					refetch();
				} else {
					notifications.show({
						title: 'Delete Failed',
						message: result.data?.deleteAdminUser?.message || 'Failed to delete user',
						color: 'red',
					});
				}
			} else if (selectedUsers.length > 0) {
				// Bulk delete
				await deleteUsers({
					variables: {
						input: {
							ids: selectedUsers,
						},
					},
				});
			}
		} catch {
			notifications.show({
				title: 'Delete Failed',
				message: 'An error occurred while deleting user(s)',
				color: 'red',
			});
		}
	};

	const isAllSelected = selectedUsers.length === filteredUsers.length && filteredUsers.length > 0;
	const isIndeterminate = selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length;

	const handleSort = (field: string) => {
		if (sortField === field) {
			// Toggle direction if clicking same field
			setSortDirection(sortDirection === OrderTypes.Asc ? OrderTypes.Desc : OrderTypes.Asc);
		} else {
			// Set new field and default to ASC
			setSortField(field);
			setSortDirection(OrderTypes.Asc);
		}
		setCurrentPage(1); // Reset to first page when sorting changes
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		setSelectedUsers([]); // Clear selection when changing pages
	};

	const getSortIcon = (field: string) => {
		if (sortField !== field) {
			return (
				<span className="sort-icon-wrapper">
					<span className="sort-icon-hover">
						<IconChevronDown size={14} />
					</span>
				</span>
			);
		}
		return (
			<span className="sort-icon-wrapper">
				<span
					style={{
						background: 'var(--mantine-color-gray-1)',
						borderRadius: '4px',
						padding: '2px',
						display: 'inline-flex',
						alignItems: 'center',
					}}
				>
					{sortDirection === OrderTypes.Asc ? (
						<IconChevronUp size={14} />
					) : (
						<IconChevronDown size={14} />
					)}
				</span>
			</span>
		);
	};

	return (
		<>
			<style>{sortableHeaderStyles}</style>
			<Stack>
				<Group justify="space-between">
					<div>
						<Text size="lg" fw={600}>
							User Management
						</Text>
						<Text size="sm" c="dimmed">
							Manage user accounts and permissions
						</Text>
					</div>
					<Group>
						<TextInput
							placeholder="Search users..."
							leftSection={<IconSearch size={16} />}
							value={searchValue}
							onChange={(e) => {
								setSearchValue(e.currentTarget.value);
								setCurrentPage(1); // Reset to first page when searching
							}}
							w={250}
						/>
						{selectedUsers.length > 0 && (
							<Button
								leftSection={<IconTrash size={16} />}
								color="red"
								variant="light"
								onClick={() => {
									setDeletingId(null);
									setDeleteModalOpen(true);
								}}
							>
								Delete {selectedUsers.length} selected
							</Button>
						)}
						<Button
							leftSection={<IconPlus size={16} />}
							onClick={() => setInviteModalOpen(true)}
						>
							Create User
						</Button>
					</Group>
				</Group>

				<DataContainer loading={loading} error={error}>
					<Paper p="lg" withBorder style={{ overflow: 'hidden' }}>
						<Table.ScrollContainer minWidth={1000}>
							<Table verticalSpacing="sm" highlightOnHover>
								<Table.Thead>
									<Table.Tr>
										<Table.Th w={40}>
											<Checkbox
												checked={isAllSelected}
												indeterminate={isIndeterminate}
												onChange={selectAll}
											/>
										</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('firstName')}
										>
											<Group gap={4} wrap="nowrap">
												Name
												{getSortIcon('firstName')}
											</Group>
										</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('email')}
										>
											<Group gap={4} wrap="nowrap">
												Email
												{getSortIcon('email')}
											</Group>
										</Table.Th>
										<Table.Th>Role</Table.Th>
										<Table.Th>Status</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('createdAt')}
										>
											<Group gap={4} wrap="nowrap">
												Created
												{getSortIcon('createdAt')}
											</Group>
										</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('updatedAt')}
										>
											<Group gap={4} wrap="nowrap">
												Last Active
												{getSortIcon('updatedAt')}
											</Group>
										</Table.Th>
										<Table.Th w={80}>Actions</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{filteredUsers.length === 0 ? (
										<Table.Tr>
											<Table.Td colSpan={8}>
												<Stack align="center" gap="xl" py={60}>
													<IconUsers size={80} stroke={0.8} />
													<Stack align="center" gap="sm">
														<Text size="lg" fw={600} c="gray.7">
															No users found
														</Text>
														<Text
															size="sm"
															c="dimmed"
															ta="center"
															maw={350}
														>
															{searchValue
																? 'No users match your search criteria. Try adjusting your filters.'
																: 'Start building your team by creating admin users.'}
														</Text>
													</Stack>
												</Stack>
											</Table.Td>
										</Table.Tr>
									) : (
										filteredUsers.map((orgUser) => (
											<Table.Tr
												key={orgUser.id}
												style={{ cursor: 'pointer' }}
												onClick={() => {
													setEditingUser(orgUser);
													setEditModalOpen(true);
												}}
											>
												<Table.Td onClick={(e) => e.stopPropagation()}>
													<Checkbox
														checked={selectedUsers.includes(orgUser.id)}
														onChange={(e) => {
															if (e.currentTarget.checked) {
																setSelectedUsers((prev) => [
																	...prev,
																	orgUser.id,
																]);
															} else {
																setSelectedUsers((prev) =>
																	prev.filter(
																		(id) => id !== orgUser.id
																	)
																);
															}
														}}
													/>
												</Table.Td>
												<Table.Td>
													<Group gap="sm">
														<Avatar size="sm" radius="md">
															{orgUser.firstName?.charAt(0)}
															{orgUser.lastName?.charAt(0)}
														</Avatar>
														<Text>
															{orgUser.firstName} {orgUser.lastName}
														</Text>
													</Group>
												</Table.Td>
												<Table.Td>
													<Text c="dimmed">
														{orgUser.authenticationUser?.email ||
															'No email'}
													</Text>
												</Table.Td>
												<Table.Td>
													<Badge
														variant="light"
														color={
															orgUser.authenticationUser?.role ===
															AuthRoleEnum.Admin
																? 'blue'
																: 'gray'
														}
													>
														{orgUser.authenticationUser?.role ||
															'No role'}
													</Badge>
												</Table.Td>
												<Table.Td>
													{orgUser.authenticationUser?.email ? (
														<Badge color="green" variant="light">
															Active
														</Badge>
													) : (
														<Badge color="yellow" variant="light">
															Pending
														</Badge>
													)}
												</Table.Td>
												<Table.Td>
													<Text c="dimmed">
														{formatDateForTable(orgUser.createdAt)}
													</Text>
												</Table.Td>
												<Table.Td>
													<Text c="dimmed">
														{orgUser.authenticationUser?.lastActive
															? formatDateForTable(
																	orgUser.authenticationUser
																		.lastActive
																)
															: '-'}
													</Text>
												</Table.Td>
												<Table.Td onClick={(e) => e.stopPropagation()}>
													<Menu position="bottom-end" withinPortal>
														<Menu.Target>
															<ActionIcon
																variant="subtle"
																color="gray"
																data-mantine-stop-propagation
															>
																<IconDots size={16} />
															</ActionIcon>
														</Menu.Target>
														<Menu.Dropdown>
															<Menu.Item
																leftSection={<IconEdit size={14} />}
																color="#1A4D66"
																onClick={() => {
																	setEditingUser(orgUser);
																	setEditModalOpen(true);
																}}
															>
																Edit
															</Menu.Item>
															<Menu.Divider />
															<Menu.Item
																color="red"
																leftSection={
																	<IconTrash size={14} />
																}
																onClick={() => {
																	setDeletingId(orgUser.id);
																	setDeleteModalOpen(true);
																}}
															>
																Delete
															</Menu.Item>
														</Menu.Dropdown>
													</Menu>
												</Table.Td>
											</Table.Tr>
										))
									)}
								</Table.Tbody>
							</Table>
						</Table.ScrollContainer>
					</Paper>
				</DataContainer>

				{/* Pagination */}
				{data?.getAllAdminUser?.pageInfo && (
					<Group justify="space-between" align="center" mt="md">
						<Text size="sm" c="dimmed">
							Showing{' '}
							{Math.min(
								(currentPage - 1) * pageSize + 1,
								data.getAllAdminUser.pageInfo.totalCount
							)}{' '}
							-{' '}
							{Math.min(
								currentPage * pageSize,
								data.getAllAdminUser.pageInfo.totalCount
							)}{' '}
							of {data.getAllAdminUser.pageInfo.totalCount} items
						</Text>
						<Pagination
							value={currentPage}
							onChange={handlePageChange}
							total={Math.max(1, data.getAllAdminUser.pageInfo.totalPages)}
							size="sm"
						/>
					</Group>
				)}

				{/* Delete Confirmation Modal */}
				<Modal
					opened={deleteModalOpen}
					onClose={() => {
						setDeleteModalOpen(false);
						setDeletingId(null);
					}}
					title="Delete Users"
					centered
				>
					<Stack gap="md">
						<Text size="sm">
							Are you sure you want to delete{' '}
							{deletingId
								? 'this user'
								: `${selectedUsers.length} selected user${selectedUsers.length > 1 ? 's' : ''}`}
							? This action cannot be undone.
						</Text>
						{(deletingId || selectedUsers.length > 0) && (
							<Paper p="sm" radius="sm" withBorder>
								<Text size="xs" c="dimmed" mb="xs">
									Users to be deleted:
								</Text>
								<Stack gap="xs">
									{filteredUsers
										.filter((user) =>
											deletingId
												? user.id === deletingId
												: selectedUsers.includes(user.id)
										)
										.slice(0, 5)
										.map((user) => (
											<Group key={user.id} gap="xs">
												<Text size="sm">
													{user.firstName} {user.lastName}
												</Text>
												<Text size="xs" c="dimmed">
													(
													{user.authenticationUser?.email ||
														`ID: ${user.userId}`}
													)
												</Text>
											</Group>
										))}
									{!deletingId && selectedUsers.length > 5 && (
										<Text size="xs" c="dimmed" fs="italic">
											and {selectedUsers.length - 5} more...
										</Text>
									)}
								</Stack>
							</Paper>
						)}
						<Group justify="flex-end" gap="sm">
							<Button
								variant="default"
								onClick={() => {
									setDeleteModalOpen(false);
									setDeletingId(null);
								}}
							>
								Cancel
							</Button>
							<Button color="red" onClick={confirmDelete}>
								Delete{' '}
								{deletingId
									? 'User'
									: `${selectedUsers.length} User${selectedUsers.length > 1 ? 's' : ''}`}
							</Button>
						</Group>
					</Stack>
				</Modal>

				{/* Create User Modal */}
				<InviteUserModal
					opened={inviteModalOpen}
					onClose={() => setInviteModalOpen(false)}
					onSuccess={() => {
						refetch();
						setInviteModalOpen(false);
					}}
				/>

				{/* Edit User Modal */}
				<EditUserModal
					opened={editModalOpen}
					onClose={() => {
						setEditModalOpen(false);
						setEditingUser(null);
					}}
					onSuccess={() => {
						refetch();
						setEditModalOpen(false);
						setEditingUser(null);
					}}
					user={editingUser}
				/>
			</Stack>
		</>
	);
}
